/**
 * Property-level optimization insights card.
 * Shows upgrade recommendations from property_recommendations table,
 * with sizing, payback, and confidence. Falls back to curtailment-only
 * display if no recommendations exist yet.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  BatteryCharging, Sun, Clock, Lightbulb, Check, X, Info, AlertTriangle, CalendarPlus,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatCurtailmentDollars } from '@/components/dashboard/CurtailmentCard';
import { cn } from '@/lib/utils';
import { useApp } from '@/contexts/AppContext';
import { useCreateIssueFromRecommendation } from '@/hooks/useServiceIssues';
import { ScheduleVisitDialog } from '@/components/visits/ScheduleVisitDialog';
import {
  buildRecommendationIssueTitle,
  buildRecommendationIssueDescription,
  buildRecommendationTriggerSnapshot,
  mapRecommendationToIssueType,
  mapRecommendationToSeverity,
  mapRecommendationToVisitType,
  buildVisitInternalNotes,
} from '@/lib/recommendation-actions';

interface Recommendation {
  id: string;
  recommendation_type: string;
  priority: string;
  annual_waste_dollars: number | null;
  annual_avoidable_import_dollars: number | null;
  suggested_addition_kwh: number | null;
  suggested_addition_kw: number | null;
  estimated_annual_savings: number | null;
  estimated_cost: number | null;
  estimated_payback_years: number | null;
  confidence: string;
  data_days: number;
  rate_source: string;
  overnight_breach_rate: number | null;
  evaluated_at: string;
}

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; label: string; headline: (r: Recommendation) => string }> = {
  battery_expansion: {
    icon: <BatteryCharging className="w-5 h-5 text-wt-amber-primary" />,
    label: 'Add battery storage',
    headline: (r) => {
      const waste = r.annual_waste_dollars ? formatCurtailmentDollars(r.annual_waste_dollars) : '$0';
      const size = r.suggested_addition_kwh ? `${r.suggested_addition_kwh} kWh` : 'additional';
      return `Your system wastes ~${waste}/year — adding ${size} battery could capture this`;
    },
  },
  pv_addition: {
    icon: <Sun className="w-5 h-5 text-wt-amber-primary" />,
    label: 'Add solar',
    headline: (r) => {
      const importCost = r.annual_avoidable_import_dollars ? formatCurtailmentDollars(r.annual_avoidable_import_dollars) : '$0';
      const size = r.suggested_addition_kw ? `${r.suggested_addition_kw} kW` : 'additional';
      return `Grid imports cost ~${importCost}/year — adding ${size} solar could offset this`;
    },
  },
  tou_shift: {
    icon: <Clock className="w-5 h-5 text-blue-400" />,
    label: 'Adjust schedule',
    headline: () => 'Your TOU schedule could be optimized to reduce peak-rate imports',
  },
  load_management: {
    icon: <Lightbulb className="w-5 h-5 text-blue-400" />,
    label: 'Manage loads',
    headline: () => 'Load shifting could reduce grid dependence',
  },
  no_action: {
    icon: <Check className="w-5 h-5 text-wt-green-primary" />,
    label: 'Well-sized',
    headline: () => 'Your system is well-sized for current usage',
  },
};

const PRIORITY_CONFIG: Record<string, { color: string }> = {
  high: { color: 'text-red-400 border-red-400/30 bg-red-500/10' },
  medium: { color: 'text-amber-400 border-amber-400/30 bg-amber-500/10' },
  low: { color: 'text-blue-400 border-blue-400/30 bg-blue-500/10' },
  info: { color: 'text-zinc-400 border-zinc-400/30 bg-zinc-500/10' },
};

const CONFIDENCE_TOOLTIP: Record<string, string> = {
  high: '90+ days of data with a verified electricity rate',
  medium: '30+ days of data or using state-average rate',
  low: 'Less than 30 days of data — estimates will improve over time',
};

interface OptimizationInsightsCardProps {
  propertyId: string;
  isOperator?: boolean;
  /** Fallback: curtailment data when no recommendations exist yet */
  curtailmentKwh?: number;
  curtailmentDollars?: number;
}

export function OptimizationInsightsCard({
  propertyId,
  isOperator,
  curtailmentKwh,
  curtailmentDollars,
}: OptimizationInsightsCardProps) {
  const queryClient = useQueryClient();
  const { organization, properties } = useApp();
  const createIssue = useCreateIssueFromRecommendation();
  const [creatingIssue, setCreatingIssue] = useState<string | null>(null);
  const [visitRec, setVisitRec] = useState<Recommendation | null>(null);

  const propertyName = properties.find((p) => p.id === propertyId)?.name ?? 'Unknown';

  const handleCreateIssue = (rec: Recommendation) => {
    if (!organization?.id) return;
    setCreatingIssue(rec.id);
    const ctx = { ...rec, property_id: propertyId, property_name: propertyName };
    createIssue.mutate(
      {
        propertyId,
        organizationId: organization.id,
        recommendationId: rec.id,
        issueType: mapRecommendationToIssueType(rec.recommendation_type),
        severity: mapRecommendationToSeverity(rec.priority),
        title: buildRecommendationIssueTitle(ctx),
        description: buildRecommendationIssueDescription(ctx),
        triggerSnapshot: buildRecommendationTriggerSnapshot(ctx),
      },
      {
        onSuccess: () => {
          toast.success('Service issue created');
          queryClient.invalidateQueries({ queryKey: ['property-recommendations', propertyId] });
        },
        onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
        onSettled: () => setCreatingIssue(null),
      },
    );
  };

  const { data: recs, isLoading } = useQuery({
    queryKey: ['property-recommendations', propertyId],
    queryFn: async (): Promise<Recommendation[]> => {
      const { data, error } = await supabase
        .from('property_recommendations')
        .select('*')
        .eq('property_id', propertyId)
        .is('dismissed_at', null)
        .order('priority', { ascending: true })
        .limit(5);
      if (error) throw error;
      return (data ?? []) as unknown as Recommendation[];
    },
    enabled: !!propertyId,
    staleTime: 10 * 60 * 1000,
  });

  const handleDismiss = async (recId: string, reason: string) => {
    try {
      const { error } = await supabase
        .from('property_recommendations')
        .update({
          dismissed_at: new Date().toISOString(),
          dismissal_reason: reason,
        })
        .eq('id', recId);
      if (error) throw error;
      toast.success('Recommendation dismissed');
      queryClient.invalidateQueries({ queryKey: ['property-recommendations', propertyId] });
    } catch {
      toast.error('Failed to dismiss');
    }
  };

  if (isLoading) return null;

  // No recommendations yet — show curtailment fallback if available
  if (!recs || recs.length === 0) {
    if (!curtailmentKwh || curtailmentKwh <= 0) return null;
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-3 px-4">
          <p className="text-sm font-medium text-foreground">
            {curtailmentDollars && curtailmentDollars > 0
              ? `${formatCurtailmentDollars(curtailmentDollars)} curtailed this month`
              : `${curtailmentKwh.toFixed(1)} kWh curtailed this month`}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Energy your system produced but couldn't use or sell
          </p>
        </CardContent>
      </Card>
    );
  }

  // Filter: don't show no_action unless it's the only one
  const actionable = recs.filter((r) => r.recommendation_type !== 'no_action');
  const display = actionable.length > 0 ? actionable : recs.slice(0, 1);

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
          <Lightbulb className="h-4 w-4" />
          Optimization Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {display.map((rec) => {
          const config = TYPE_CONFIG[rec.recommendation_type] ?? TYPE_CONFIG.no_action;
          const priorityCfg = PRIORITY_CONFIG[rec.priority] ?? PRIORITY_CONFIG.info;

          return (
            <div key={rec.id} className="p-3 rounded-md border border-border/50 bg-wt-bg-tertiary/30 space-y-2">
              <div className="flex items-start gap-3">
                {config.icon}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline" className={cn('text-[10px]', priorityCfg.color)}>
                      {rec.priority}
                    </Badge>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-wt-bg-tertiary text-muted-foreground">
                      {config.label}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-[10px] cursor-help border-wt-border-subtle text-muted-foreground">
                          {rec.confidence} confidence
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs">
                        {CONFIDENCE_TOOLTIP[rec.confidence] ?? 'Confidence based on data quality and rate source'}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-sm text-foreground">{config.headline(rec)}</p>
                </div>
              </div>

              {/* Details row */}
              {rec.recommendation_type !== 'no_action' && (
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pl-8">
                  {rec.estimated_annual_savings != null && (
                    <span>
                      Est. savings: <span className="text-wt-green-primary font-medium">{formatCurtailmentDollars(rec.estimated_annual_savings)}/yr</span>
                    </span>
                  )}
                  {rec.estimated_cost != null && (
                    <span>Est. cost: ${rec.estimated_cost.toLocaleString()}</span>
                  )}
                  {rec.estimated_payback_years != null && (
                    <span>Payback: {rec.estimated_payback_years} yr{rec.estimated_payback_years !== 1 ? 's' : ''}</span>
                  )}
                  {rec.rate_source === 'state_average' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-1 cursor-help">
                          <Info className="w-3 h-3" /> Using state avg rate
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-xs">
                        Set a manual rate in Settings for more accurate estimates.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              )}

              {/* Operator actions */}
              {isOperator && rec.recommendation_type !== 'no_action' && (
                <div className="flex gap-2 pl-8">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground"
                    onClick={() => handleCreateIssue(rec)}
                    disabled={creatingIssue === rec.id}
                  >
                    <AlertTriangle className="h-3 w-3 mr-1" /> Create Issue
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground"
                    onClick={() => setVisitRec(rec)}
                  >
                    <CalendarPlus className="h-3 w-3 mr-1" /> Schedule Visit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground"
                    onClick={() => handleDismiss(rec.id, 'operator_dismissed')}
                  >
                    <X className="h-3 w-3 mr-1" /> Dismiss
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>

      {/* Schedule Visit dialog — pre-filled from recommendation */}
      {visitRec && (
        <ScheduleVisitDialog
          open={!!visitRec}
          onOpenChange={(open) => { if (!open) setVisitRec(null); }}
          preselectedPropertyId={propertyId}
          prefilledVisitType={mapRecommendationToVisitType(visitRec.recommendation_type)}
          prefilledInternalNotes={buildVisitInternalNotes({
            ...visitRec,
            property_id: propertyId,
            property_name: propertyName,
          })}
        />
      )}
    </Card>
  );
}
